import React from "react";
import {produce} from "immer";
import {Actions, ActionType, State, VisibleLegsReducer} from "../state";
import * as Util from "../util";
import {isFlightScheduled} from "../util";
import {difference, union} from "lodash";
import {Phase2ScheduleNode} from "../types";
import {getSNodeID} from "../../../../../components/scheduling/util";
import {ScheduleNode} from "../../../../../components/scheduling/types";
import {Phase2Api} from "../api";
import {SET_SELECTED_ENTITY_IDS, SET_STATUS, SET_VISIBLE, SET_VISIBLE_LEGS} from "./action";
import {useMutationDispatcher} from "./invoke-mutation";
import {useHistory} from "react-router-dom";
import {getSelectedEntities} from "./util";
import {message} from "antd";

export type { SchedToExpAction } from './action';

export const DefaultState = {
    modal: {
        open: false
    },
    data: {
        entities: [] as Phase2ScheduleNode[]
    },
    selection: {
        selectedEntityIds: [] as string[]
    },
    editor: {
        visibleLegs: [] as { departureID: string, destinationID: string }[]
    },
    status: {
        isLoading: false,
        error: null as Error
    }
}

export type SchedToExpState = typeof DefaultState;

export const useApi = (api: Phase2Api) => {

    const _state = api.getState();
    const dispatch = api.getDispatch();
    const state = _state.scheduledToExpectedPaxCgo;
    const history = useHistory();

    const dispatchMutation = useMutationDispatcher(api);

    const scheduledToExpectedPaxCgoApi = {
        modal: {
            isOpen: () => state.modal.open,
            setOpen: (open: boolean) => dispatch({
                type: SET_VISIBLE,
                payload: { open: open }
            })
        },
        isLoading: state.status.isLoading,
        selectedEntityIds: state.selection.selectedEntityIds as Readonly<string[]>,
        start: () => {
            const selectedEntities = getSelectedEntities(api);
            const allEntities = state.data.entities;

            const hasPax = selectedEntities.findIndex((e) => e.classType === 'flytsuite.paxnode') > -1;
            const hasCgo = selectedEntities.findIndex((e) => e.classType === 'flytsuite.cgonode') > -1;

            dispatchMutation()
                .then(() => {
                    message.success(`Successfully made ${allEntities.length} PAX/CGO Expected.`);
                    if (selectedEntities.length === allEntities.length){
                        // All entities were modified. This schedule no longer exists. Redirect to pax or cgo grids.
                        if (!hasPax && hasCgo){
                            history.push('/app/cargo');
                        }
                        else {
                            history.push('/app/passengers');
                        }
                        return;
                    }
                    // Otherwise stay on the current screen and refresh the data.
                    scheduledToExpectedPaxCgoApi.modal.setOpen(false);
                    api.data?.refetch();
                })
                .catch((err) => {
                    console.error(err);
                    message.error("Failed to make pax/cgo expected. View console for details.");
                })
        }
    }

    return scheduledToExpectedPaxCgoApi;
}

export type Api = ReturnType<typeof useApi>

export const Reducer: React.Reducer<State, Actions>
    = (prevState, action) => produce(prevState, (draft) => {

    const state = draft.scheduledToExpectedPaxCgo;

    function initData(){
        const entitiesNotOnValidFlightOnly = prevState.data.originalEntities
            .filter((entity) => !isFlightScheduled(entity.currentCarrierID));

        state.data.entities = entitiesNotOnValidFlightOnly;

        state.selection.selectedEntityIds = [];

        const data = Util.DepDestList.build(entitiesNotOnValidFlightOnly);

        const depDestList = data
            .map(item => ({
                departureID: item.departure._id,
                destinationID: item.destination._id
            }));

        state.editor.visibleLegs = depDestList;
    }

    switch (action.type){
        case ActionType.INIT_DATA:
            if (!prevState.scheduledToExpectedPaxCgo.modal.open) return;
            initData();
            break;
        case ActionType.RESET_DATA:
            if (!prevState.scheduledToExpectedPaxCgo.modal.open) return;
            initData();
            break;
        case SET_VISIBLE:
            if (action.payload.open == state.modal.open) return;
            state.modal.open = action.payload.open;
            initData();

            // Select all entities upon opening
            state.selection.selectedEntityIds = state
                .data.entities.map(entity => getSNodeID(entity as ScheduleNode));
            break;
        case SET_VISIBLE_LEGS:
            state.editor.visibleLegs = VisibleLegsReducer(state.editor.visibleLegs, {
                ...action,
                type: ActionType.SET_VISIBLE_LEGS
            })
            break;
        case SET_SELECTED_ENTITY_IDS:
            if (!action.add && !action.remove && !action.set) return;

            let newSelEntityIds = state.selection.selectedEntityIds;

            if (action.set){
                newSelEntityIds = action.set;
            }
            if (action.remove){
                newSelEntityIds = difference(newSelEntityIds, action.remove);
            }
            if (action.add){
                newSelEntityIds = union(newSelEntityIds, action.add);
            }

            state.selection.selectedEntityIds = newSelEntityIds;
            break;
        case SET_STATUS:
            const { status } = draft.scheduledToExpectedPaxCgo;

            switch (action.payload.status){
                case 'SUCCESS':
                    status.isLoading = false;
                    status.error = null;
                    break;
                case 'LOADING':
                    status.isLoading = true;
                    status.error = null;
                    break;
                case 'ERROR':
                    status.isLoading = false;
                    status.error = action.payload.error;
            }
            break;
    }
})